import companyApi from 'api/auth/company/company-api';
import type { CompanyApiKeyListModel } from 'api/auth/company/models';
import type { CompanyApiAccess } from './company-api-access-type';

export interface ApiAccessService {
  getApiAccess: (companyId: number) => Promise<CompanyApiAccess[]>;
  generateKey: (companyId: number) => Promise<CompanyApiAccess>;
  rotateKey: (companyId: number) => Promise<CompanyApiAccess>;
  expireKey: (companyId: number) => Promise<CompanyApiAccess>;
  revokeKey: (companyId: number) => Promise<CompanyApiAccess>;
}

const emptyApiAccess = (companyId: number): CompanyApiAccess => ({
  companyId,
  apiKey: null,
  status: 'none',
  createdAt: null,
  expiresAt: null
});

const toIsoString = (value?: Date | string | null): string | null => {
  if (!value) {
    return null;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
};

const getCreatedTime = (apiKey: CompanyApiKeyListModel) => {
  const created = apiKey.dateCreated;
  if (!created) {
    return 0;
  }

  const parsed = created instanceof Date ? created : new Date(created);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const sortApiKeys = (apiKeys: CompanyApiKeyListModel[]) =>
  [...apiKeys].sort((left, right) => getCreatedTime(right) - getCreatedTime(left));

const mapApiKey = (companyId: number, apiKey?: CompanyApiKeyListModel): CompanyApiAccess => {
  if (!apiKey) {
    return emptyApiAccess(companyId);
  }

  const expiresAt = toIsoString(apiKey.dateRevoked);
  const expiryDate = expiresAt ? new Date(expiresAt) : null;
  const hasExpired = expiryDate !== null && expiryDate.getTime() <= Date.now();

  return {
    id: apiKey.id,
    companyId: apiKey.companyId ?? companyId,
    apiKey: apiKey.apiKey ?? null,
    status: hasExpired ? 'expired' : 'active',
    createdAt: toIsoString(apiKey.dateCreated),
    expiresAt,
  };
};

const unsupportedApiKeyMutation = async (): Promise<CompanyApiAccess> => {
  throw new Error('Company API key mutations are not supported by the auth API client yet.');
};

export const apiAccessService: ApiAccessService = {
  async getApiAccess(companyId) {
    const apiKeys = await companyApi.getCompanyApiKeys(companyId);
    const mappedApiKeys = sortApiKeys(apiKeys).map((apiKey) => mapApiKey(companyId, apiKey));
    return mappedApiKeys.length ? mappedApiKeys : [emptyApiAccess(companyId)];
  },
  generateKey: unsupportedApiKeyMutation,
  rotateKey: unsupportedApiKeyMutation,
  expireKey: unsupportedApiKeyMutation,
  revokeKey: unsupportedApiKeyMutation,
};
